import React from 'react';
import { ContentArea, ListLowerLatin, ListLowerRoman } from './styles';

type Props = { className?: string };

const TermsAndConditions = ({ className }: Props) => (
  <ContentArea className={className}>
    <p>Wi-Q OPEG TERMS AND CONDITIONS</p>

    <p>PLEASE READ THIS CAREFULLY BEFORE USING THIS OPEG OR WEBSITE.</p>

    <h3>1. Parties</h3>

    <p>
      This end-user licence agreement (the “Agreement”) is a legal agreement between you (“You” or “Your”) and
      "WI-Q LIMITED", a company registered in England and Wales with company registration number 09209151
      whose registered office is at Studio 1.19, One Silk Street, Ancoats Urban Village, Manchester, M4 6LZ,
      GB (referred to in this Agreement as “Us”, “We”, or “Our”). Our VAT number is 199 2137 74.
    </p>

    <h3>2. Definitions</h3>

    <p>In this Agreement the following definitions apply:</p>
    <p>
      <strong>Device:</strong> the device that You use to access Our Site, including but not limited to
      tablet, notebook, netbook, laptop, mobile phone, smartphone, pda, tablet, or any other device that is
      capable of accessing Our Site whether or not You actually own the device.
    </p>
    <p>
      <strong>OPEG:</strong> means Our website as accessed by You with Your Device.
    </p>
    <p>
      <strong>Order:</strong> Your order for Products from the Outlet as placed through the OPEG.
    </p>
    <p>
      <strong>Outlet:</strong> The outlet specified in the OPEG as the provider of the Products.
    </p>
    <p>
      <strong>Products:</strong> Those products offered by the Outlet for Your purchase through the OPEG.
    </p>
    <p>
      <strong>Services:</strong> The services that We provide to You from time to time through Our Site on the
      terms and conditions of this Agreement, namely ordering of food and drink items from Outlets.
    </p>
    <p>
      <strong>Site:</strong> Our website, OPEG, and or other software through which You may order Products
      from the Outlet including but not limited to Our website{' '}
      <a href="https://www.wi-q.com" target="_blank">
        http://www.wi-q.com
      </a>{' '}
      and all references include any current or future version of Our website or OPEG or other software
      through which You access Our Services, in each case whether accessed through any current or future
      platform or Device.
    </p>

    <h3>3. Status of Agreement</h3>

    <ListLowerLatin>
      <li>
        This Agreement sets out the terms and conditions on which We provide Our Services to You using Your
        Device to access Our Site.
      </li>
      <li>
        Please read this Agreement carefully before placing Your Order as Your purchase is subject to this
        Agreement.
      </li>
      <li>
        By clicking on the "Accept" button below You agree to the terms of this Agreement which will bind You.
        If You do not agree to the terms of this Agreement You must click on the "Cancel" button below and You
        will not be able to access Our Services.
      </li>
      <li>
        You accept responsibility in accordance with the terms of this Agreement for the use of the Site or
        any Service whether or not You own the Device You are using.
      </li>
      <li>
        We reserve the right to change this Agreement from time to time. We will notify You if We change any
        part of this Agreement with details of the change. Any changes we make to Our agreement in the future
        will be posted on this page and where appropriate, notified to you by SMS or email or notifying You of
        a change when You next start the OPEG. Then new terms may be displayed on-screen and You may be
        required to read and accept them to continue Your use of the App or the Services
      </li>
      <li>We advise You to print or store a copy of this Agreement for future reference. </li>
      <li>
        By ordering Products via the Site (whether now or in the future), You agree to be bound by this
        Agreement including any changes to it in effect at the time of Your Order.
      </li>
      <li>We licence use of Our Site on the basis of this Agreement.</li>
      <li>
        From time to time We will update the OPEG. Depending on the update, You may not be able to use the
        OPEG or the Services until You have accepted any new terms.
      </li>
      <li>We remain the owners of the Site at all times. </li>
      <li>
        The terms of Our privacy policy (Our “Privacy Policy” can be found at{' '}
        <a href="https://discoverwiq.wi-q.com/privacy_policy" target="_blank">
          https://discoverwiq.wi-q.com/privacy_policy
        </a>
        ).
      </li>
      <li>
        By using Our Site and or the OPEG You acknowledge and agree that internet transmissions are never
        completely private or secure. You understand that any message or information You send using the Site
        may be read or intercepted by others, even if there is a special notice that a particular transmission
        is encrypted.
      </li>
      <li>
        By using Our Site and or the OPEG or any of the Services, You consent to Us collecting and using
        technical information about Your Device and related software.
      </li>
      <li>
        Certain Services will make use of location data sent from Your Device. If You use the Services, You
        consent to Us and Our affiliates' and licensees' transmission, collection, maintenance, processing and
        use of Your location data.
      </li>
      <li>You are responsible for making all arrangements necessary for You to have access to Our Site.</li>
      <li>
        You are also responsible for ensuring that all persons who access the Site through Your Device are
        aware of this Agreement and that they comply with them as You will be responsible for their actions.
      </li>
      <li>
        The Site the OPEG or any Service may contain links to other independent third-party websites (“Third
        Party Sites”). Third Party Sites are not under Our control, and We are not responsible for and do not
        endorse their content or their privacy policies (if any). You will need to make Your own independent
        judgement regarding Your interaction with any Third Party Sites, including the purchase and use of any
        products or services accessible through them. For the avoidance of doubt, we are not responsible for
        inaccurate or incorrect information about Products sold by Third Party Sites via the OPEG or Our Site.
        If you require further information (including any dietary or allergen information not shown) or advice
        about any Product or service available on the OPEG or on our Site please contact the relevant Third
        Party Site for more information.
      </li>
      <li>
        By using Our Site and or the OPEG You consent to Us and Our affiliates' and licensees' sending You
        messages including but not limited to those of an advertisement or promotional nature.
      </li>
    </ListLowerLatin>

    <h3>4. Grant and scope of licence</h3>

    <ListLowerLatin>
      <li>
        In consideration of You agreeing to abide by the terms of this Agreement We grant You a
        non-transferable, non-exclusive, non-sub licensable licence to use the OPEG on the Device, subject to
        these terms and the Privacy Policy{' '}
        <a href="https://discoverwiq.wi-q.com/privacy_policy">https://discoverwiq.wi-q.com/privacy_policy</a>.
        We reserve all other rights.
      </li>
    </ListLowerLatin>

    <h3>5. Site Access</h3>

    <ListLowerLatin>
      <li>
        We aim to ensure that the Site and or the OPEG is available at all times but We do not undertake any
        obligation to do so or warrant that it will be. We will not be liable to You if the Site is
        unavailable at any time or for any period.
      </li>
      <li>
        We may temporarily suspend Your access to the Site and or the OPEG at any time and without notice.
      </li>
    </ListLowerLatin>

    <h3>6. Restrictions</h3>

    <p>Except as expressly set out in this Agreement or as permitted by any local law, You agree</p>

    <ListLowerLatin>
      <li>
        not to copy the Site and or the OPEG in whole or in part except where such copying is incidental to
        normal use of the Site and or the OPEG, or where it is necessary for the purpose of back-up or
        operational security and then to the minimum extent and for the minimum period necessary;
      </li>
      <li>
        not to make alterations to, or modifications of, the whole or any part of the Site or the OPEG, or
        permit the Site or the OPEG or any part of it to be combined with, or become incorporated in, any
        other programs;
      </li>
      <li>
        not to provide or otherwise make available the Site and or the OPEG in whole or in part (including
        object and source code), in any form to any person without Our prior written consent;
      </li>
      <li>
        not use the Site and or the OPEG or any Service in any unlawful manner, for any unlawful purpose, or
        in any manner inconsistent with this Agreement, or act fraudulently or maliciously, for example, by
        hacking into or inserting malicious code, including viruses, or harmful data, into the Site and or the
        OPEG, any Service or any operating system;
      </li>
      <li>
        not infringe Our intellectual property rights or those of any third party in relation to Your use of
        the Site and or the OPEG or any Service;
      </li>
      <li>
        not transmit any material that is defamatory, offensive or otherwise objectionable in relation to Your
        use of the Site and or the OPEG or any Service;
      </li>
      <li>
        not use the Site and or the OPEG or any Service in a way that could damage, disable, overburden,
        impair or compromise Our systems or security or interfere with other users or any other person;
      </li>
      <li>
        not collect or harvest any information or data from any Service or Our systems or attempt to decipher
        any transmissions to or from the servers running any Service; and
      </li>
      <li>
        You are not allowed to disassemble, decompile, reverse-engineer, or create derivative works based on
        any part of the Site or OPEG. However, if you need to do these things to make the Site work with
        another software program, you can, as long as:
        <ListLowerRoman>
          <li>You only use the information to make the Site work with another software program.</li>
          <li>You don't share the information with anyone else without our written permission.</li>
          <li>You don't use the information to create software that is very similar to OPEG.</li>
        </ListLowerRoman>
      </li>
    </ListLowerLatin>

    <h3>7. Your Status</h3>

    <ListLowerLatin>
      <li>
        By placing an Order through the OPEG, You warrant that:
        <ListLowerRoman>
          <li>You are legally capable of entering into binding contracts; and</li>
          <li>You have the means to pay for Your Order; and</li>
          <li>You are at least 18 years old.</li>
        </ListLowerRoman>
      </li>
      <li>
        Acknowledgment and Agreement: You acknowledge and agree that:
        <ListLowerRoman>
          <li>
            Offense and Prohibition:
            <ul>
              <li>
                It is an offense for any person under the local age limit to buy or attempt to buy
                intoxicating liquor.
              </li>
              <li>
                It is also an offense for any person over the local age limit to purchase intoxicating liquor
                on behalf of any person under the age of the local age limit.
              </li>
            </ul>
          </li>
          <li>
            Order Restrictions:
            <ul>
              <li>
                Orders containing alcohol cannot be accepted from or on behalf of persons under the local age
                limit.
              </li>
            </ul>
          </li>
        </ListLowerRoman>
      </li>
    </ListLowerLatin>

    <h3>8. Your Order</h3>

    <ListLowerLatin>
      <li>
        Once You have selected the Products You wish to order from the Outlet and provided the other required
        information, You will be given the opportunity to submit Your Order by clicking or selecting the
        "place my order" button.
      </li>
      <li>Once You have submitted Your Order You will not be entitled to change or cancel it.</li>
      <li>
        Where any payment You make is not authorised, Your Order will not be processed or communicated to the
        relevant Outlet.
      </li>
      <li>
        Where any payment you make is authorised, We will send Your Order to the relevant Outlet and will
        notify You that Your Order has been received and is being processed.
      </li>
      <li>
        Such notification merely indicates that Your Order has been received and is being processed, and does
        not necessarily mean that the Outlet has accepted Your Order.
      </li>
      <li>The Outlet is free to accept or reject Your Order as it sees fit.</li>
      <li>
        We will notify You (generally by email) as soon as reasonably practicable if an Outlet rejects Your
        Order.
      </li>
      <li>
        In the event that an Outlet rejects Your Order neither the Outlet or We are required to inform You of
        the reason. Adhering to the merchants’ refund policy you will be credited accordingly.
      </li>
    </ListLowerLatin>

    <h3>9. Status of Your Order</h3>

    <ListLowerLatin>
      <li>Your legal contract for the supply and purchase of Products is between You and the Outlet.</li>
      <li>We have no control over the actions or omissions of any Outlet.</li>
      <li>
        You have a legal right to receive the Products ordered which comply with their description, which are
        of satisfactory quality and which comply with any specific requirements You tell Us about (and the
        Outlet agrees to) before You place Your order. If you think there is something wrong with the Products
        You ordered We will refer You to the Outlet who will work with You to resolve the issue.
      </li>
    </ListLowerLatin>

    <h3>10. Price and Payment</h3>

    <ListLowerLatin>
      <li>
        Product prices may be shown either inclusive or exclusive of VAT, depending on the configuration set
        by the Outlet. Unless otherwise stated, prices exclude service charges or gratuities, which You may
        add at Your discretion.
      </li>
      <li>
        On occasion, it is possible that the Site may contain an incorrect Product price. If there is an
        obvious mistake in the Product price We will notify as soon as We can and You will have the choice of
        confirming the order at the correct Product price or cancelling the order without charge and with a
        full refund of any money already paid.
      </li>
      <li>
        Payment for Orders must be made by an accepted credit or debit card through the Site or directly to
        the Outlet at the point of delivery to You.
      </li>
      <li>If You pay by credit or debit card, You may be required to show the card to the Outlet.</li>
      <li>
        A credit or discount may apply to Your Order if You use a promotional voucher or code and You pay for
        any balance by credit or debit card.
      </li>
    </ListLowerLatin>

    <h3>11. Intellectual property rights</h3>

    <ListLowerLatin>
      <li>
        You acknowledge that all intellectual property rights in the Site and the OPEG anywhere in the world
        belong to Us or Our licensors, that rights in the Site and the OPEG are licensed (not sold) to You,
        and that You have no rights in, or to, the Site and the OPEG other than the right to use the Site and
        the OPEG in accordance with the terms of this Agreement.
      </li>
      <li>You acknowledge that You have no right to have access to the OPEG in source-code form.</li>
    </ListLowerLatin>

    <h3>12 a. Limitation of Liability (Consumer Customers)</h3>

    <ListLowerLatin>
      <li>
        You understand that the Site and the OPEG are designed for general use and may not meet your
        individual needs. It is your responsibility to ensure that the Site and the OPEG work for you
      </li>
      <li>
        We provide the OPEG for personal and private use only. You agree not to use it for commercial,
        business, or resale purposes. Our liability for any loss You suffer in connection your trade,
        business, craft or profession is limited as described in{' '}
        <strong>12 b. Limitation of Liability (Business Customers)</strong> below.
      </li>
      <li>
        We strive to ensure the accuracy of the information on the Site and the OPEG, but we do not guarantee
        it will always be correct, complete, or up-to-date.
      </li>
      <li>
        We rely on Outlets to provide accurate allergy, dietary, and other product information. While we do
        our best to display this accurately, it is the Outlet's responsibility. If you need further
        information, please contact the Outlet before placing your order. Therefore, we are not liable for any
        errors or omissions in this information.
      </li>
      <li>
        Nothing in this Agreement limits our liability for:
        <ListLowerRoman>
          <li>Death or personal injury;</li>
          <li>Fraud or fraudulent misrepresentation; and</li>
          <li>Any other liability that cannot be excluded or limited by law.</li>
        </ListLowerRoman>
      </li>
    </ListLowerLatin>

    <h3>12 b. Limitation of liability (Business Customers)</h3>

    <ListLowerLatin>
      <li>
        You acknowledge that the Site and/or the OPEG have not been developed to meet your unique business
        requirements, and it is therefore your responsibility to ensure that the facilities and functions of
        the Site and/or the OPEG meet your business needs.
      </li>
      <li>
        We provide the OPEG for commercial and business use. However, we shall not be liable for any loss of
        profit, loss of business, business interruption, or loss of business opportunity arising from your use
        of the OPEG.
      </li>
      <li>
        Although we strive to ensure that the information on the Site and the OPEG is accurate, we do not
        warrant that it will always be correct, complete, or up-to-date.
      </li>
      <li>
        Where an Outlet has provided us with allergy, dietary, and other Product information, we endeavor to
        accurately reproduce this on the Site and the OPEG. However, it is the responsibility of the Outlet to
        provide this information to us, and if you require any further information, you should contact the
        Outlet directly before placing your order. Consequently, we exclude all liability for any errors or
        omissions in such information as it is the Outlet's responsibility to manage this.
      </li>
      <li>
        Our maximum aggregate liability under or in connection with this Agreement (including your use of any
        Services) whether in contract, tort (including negligence) or otherwise, shall in all circumstances be
        limited to the price actually paid by you for the Services on the occasion you suffered a loss. This
        does not apply to the types of loss set out in condition f) below.
      </li>
      <li>
        Nothing in this Agreement shall limit or exclude our liability for:
        <ListLowerRoman>
          <li>Death or personal injury;</li>
          <li>fraud or fraudulent misrepresentation; and</li>
          <li>Any other liability that cannot be excluded or limited by law.</li>
        </ListLowerRoman>
      </li>
    </ListLowerLatin>

    <h3>13. Termination</h3>

    <ListLowerLatin>
      <li>
        We may terminate this Agreement immediately by written notice to You, if:
        <ListLowerRoman>
          <li>You do not make full payment to Us when it is due; and/or</li>
          <li>You do not provide any required information to complete the Order.</li>
        </ListLowerRoman>
      </li>
      <li>
        On termination for any reason:
        <ListLowerRoman>
          <li>all rights granted to You under this Agreement shall cease;</li>
          <li>
            You must immediately cease all activities authorised by this Agreement, including Your use of any
            Services;
          </li>
          <li>
            You must immediately delete or remove the Site and the OPEG from all Devices, and immediately
            destroy all copies of the Site and the OPEG in Your possession, custody or control and, if We
            request, certify to Us that You have done so;
          </li>
          <li>
            We may remotely access the Device and remove the Site and the OPEG from all of them and cease
            providing You with access to the Services.
          </li>
          <li>
            any payment already made by You for Products that have not been provided will be refunded on
            termination.{' '}
          </li>
        </ListLowerRoman>
      </li>
    </ListLowerLatin>

    <h3>14. Communication between Us</h3>

    <p>
      If You wish to contact Us in writing, or if any condition in this Agreement requires You to give Us
      written notice, You can send this to Us by e-mail to{' '}
      <a href="mailto:info@wi-q.com" target="_blank">
        info@wi-q.com
      </a>{' '}
      or by prepaid post to Office Address:
    </p>

    <address>Studio 1.19, One Silk Street, Ancoats Urban Village, Manchester, England, M4 6LZ</address>

    <ListLowerLatin>
      <li>We will confirm receipt of this by contacting You in writing, normally by e-mail.</li>
      <li>
        If We have to contact You or give You notice in writing, We will do so by e-mail or by post to the
        email address you provided us when submitting an Order.
      </li>
    </ListLowerLatin>

    <h3>15. Events outside Our control</h3>

    <ListLowerLatin>
      <li>
        We will not be liable or responsible for any failure to perform, or delay in performance of, any of
        Our obligations under this Agreement that is caused by any act or event beyond Our reasonable control,
        including but not limited to failure of public or private telecommunications networks, public utility
        providers, or the internet (an “Event Outside Our Control”).
      </li>
      <li>
        If an Event Outside Our Control takes place that affects the performance of Our obligations under this
        Agreement:
        <ListLowerRoman>
          <li>
            Our obligations under this Agreement will be suspended and the time for performance of Our
            obligations will be extended for the duration of the Event Outside Our Control; and
          </li>
          <li>
            We will use Our reasonable endeavors to find a solution by which Our obligations under this
            Agreement may be performed despite the Event Outside Our Control.
          </li>
        </ListLowerRoman>
      </li>
    </ListLowerLatin>

    <h3>16. Other important terms</h3>

    <ListLowerRoman>
      <li>
        We may transfer Our rights and obligations under this Agreement to another organisation, but this will
        not affect Your rights or Our obligations under this Agreement.
      </li>
      <li>Your rights or obligations under this Agreement are personal.</li>
      <li>
        If We fail to insist that You perform any of Your obligations under this Agreement, or if We do not
        enforce Our rights against You, or if We delay in doing so, that will not mean that We have waived Our
        rights against You and will not mean that You do not have to comply with those obligations. If We do
        waive a default by You, We will only do so in writing, and that will not mean that We will
        automatically waive any later default by You.
      </li>
      <li>
        Each of the conditions of this Agreement operates separately. If any court or competent authority
        decides that any of them are unlawful or unenforceable, the remaining conditions will remain in full
        force and effect.
      </li>
      <li>
        This agreement and the documents and policies referred to in it constitute the whole agreement and
        understanding between You and Us and supersede and replace any previous arrangement, understanding or
        agreement between You and Us relating to its subject matter.
      </li>
      <li>
        This Agreement, its subject matter and its formation, are governed by English law. Wherever you live
        you can bring claims against us in the English courts. If you live in Wales, Scotland or Northern
        Ireland, you can also bring claims against us in the courts of the country you live in. We can claim
        against you in the courts of the country you live in.
      </li>
      <li>
        As a consumer, You will benefit from any mandatory provisions of the law of the country in which you
        are resident. Nothing in this Agreement, including the paragraph above, affects Your rights as a
        consumer to rely on such mandatory provisions of local law.
      </li>
    </ListLowerRoman>
  </ContentArea>
);

export default TermsAndConditions;
